"use client";

import clsx from "clsx";
import { useCallback, useEffect, useState } from "react";
import { LtrToken } from "@/components/cdm/LtrToken";
import { advanceScreen } from "@/lib/presenter/advanceScreen";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 1.7 — Issuance fee + plan picker (Freemium vs SME).
 *
 *   Left pane: two pricing tiles side-by-side.
 *     - Freemium / Pay-as-you-go (compare): yearly OrbitraID + per-trade
 *       service fees.
 *     - SME plan (recommended · selected by default): yearly OrbitraID
 *       + monthly services bundle (excluding LC) — saves money once you
 *       run a couple of trades a month.
 *     Below: notes (1% payment transaction fee, OrbitraID contents,
 *     SME bundle contents, "upgrade anytime").
 *
 *   Right pane: subscription summary card (plan, billing, next charge,
 *   currency, auto-renew).
 *
 *   Modal: card form, opens on button click OR auto-opens when authState
 *   leaves "ready" so the demo's event-driven auto-flow still works.
 *   The card body now leads with a Payment Summary block — yearly
 *   OrbitraID ($250) + SME monthly ($350) + 5% VAT → $630 total.
 *   Closes on Esc, click-outside, × button, or ~1.8s after "approved".
 *
 *   On approve: PaymentDispatched + PaymentReconciled events, Billing &
 *   Invoices module unlocks.
 */

// Prices (USD). Tweak in one place; the modal summary + CTA derive from these.
const ORBITRAID_YEAR    = 250;
const SME_MONTH         = 350;
const VAT_RATE          = 0.05;
const TOTAL_PRETAX      = ORBITRAID_YEAR + SME_MONTH;       // 400
const TOTAL_VAT         = TOTAL_PRETAX * VAT_RATE;          // 20
const TOTAL_GROSS       = TOTAL_PRETAX + TOTAL_VAT;         // 420

function usd(n: number): string {
  return n.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export function OrbIdFeeCheckout({
  authState = "ready",
  className,
}: {
  authState?: "ready" | "authorising" | "approved";
  className?: string;
}) {
  const [modalOpen, setModalOpen] = useState(false);
  // Local card-phase state — drives the modal button text + the summary
  // chip independently of the raw SSE timing, so the audience sees the
  // popup card → authorising → success arc rather than having the chip
  // jump on the moment SSE says authState=approved.
  const [cardPhase, setCardPhase] = useState<"idle" | "card" | "authorising" | "approved">("idle");
  const closeModal = useCallback(() => setModalOpen(false), []);

  // Per the latest amendment: the payment modal opens ONLY when the user
  // clicks the "Pay now and prepare your order…" CTA. Removed the previous
  // auto-open on authState !== "ready" so the audience reads the
  // Freemium-vs-SME tiles first, then triggers the popup themselves.

  // Drive cardPhase: idle → card (modal opens) → authorising → approved.
  useEffect(() => {
    if (!modalOpen) { setCardPhase("idle"); return; }
    setCardPhase("card");
    const t1 = setTimeout(() => setCardPhase("authorising"), 1500);
    return () => clearTimeout(t1);
  }, [modalOpen]);

  // Once SSE confirms approval AND the modal has been showing "authorising"
  // for a beat, advance to "approved".
  useEffect(() => {
    if (cardPhase !== "authorising") return;
    if (authState !== "approved") return;
    const t = setTimeout(() => setCardPhase("approved"), 1500);
    return () => clearTimeout(t);
  }, [authState, cardPhase]);

  // Auto-close ~1.8s after the modal reaches approved so the success chip
  // on the main screen takes over and the modal doesn't block the next event.
  useEffect(() => {
    if (cardPhase === "approved" && modalOpen) {
      const t = setTimeout(() => setModalOpen(false), 1800);
      return () => clearTimeout(t);
    }
  }, [cardPhase, modalOpen]);

  // Esc closes.
  useEffect(() => {
    if (!modalOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeModal();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [modalOpen, closeModal]);

  const t = useTranslations("t1Registration");
  return (
    <div className={clsx("grid h-full grid-cols-3 gap-4", className)}>
      {/* Left + middle — plan tiles + notes */}
      <div className="glass col-span-2 flex flex-col p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("feeStep")}</p>
        <h3 className="mt-1 font-display text-base font-semibold text-orbitra-paper">{t("feeTitle")}</h3>

        <div className="mt-4 grid grid-cols-2 gap-3">
          <PlanTile
            kind="freemium"
            title={t("feeFreemiumTitle")}
            subtitle={t("feeFreemiumDescription")}
            highlights={[
              { label: t("feeHighlightIdYearly"),      value: `USD ${usd(ORBITRAID_YEAR)} + 5% VAT` },
              { label: t("feeHighlightServiceFees"),   value: t("feeHighlightServiceFeesValue") },
            ]}
            footer={t("feePlanFreemiumFooter")}
            selected={false}
          />
          <PlanTile
            kind="sme"
            title={t("feeSmeTitle")}
            subtitle={t("feeSmeDescription")}
            highlights={[
              { label: t("feeHighlightIdYearly"),        value: `USD ${usd(ORBITRAID_YEAR)} + 5% VAT` },
              { label: t("feeHighlightServicesMonthly"), value: `USD ${usd(SME_MONTH)} + 5% VAT` },
            ]}
            footer={t("feePlanSmeFooter")}
            selected
          />
        </div>

        <div className="mt-4 grid grid-cols-2 gap-2">
          <Note label={t("feeNoteTxnFeeLabel")}>{t("feeAppliesPerTrade")}</Note>
          <Note label={t("feeOrbitraIdIncludes")}>{t("feeOrbitraIdBundle")}</Note>
          <Note label={t("feeSmeBundleIncludes")}>{t("feeSmeBundle")}</Note>
          <Note label={t("feeUpgradeAnytime")}>{t("feeUpgradeDetail")}</Note>
        </div>

        <button
          onClick={() => setModalOpen(true)}
          className="cta-primary mt-auto py-2.5 text-[12px]"
        >
          {t("feePayCta")} {usd(TOTAL_GROSS)}
        </button>
      </div>

      {/* Right — subscription summary */}
      <div className="glass flex flex-col p-5">
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{t("feeSubscriptionSummary")}</p>
        <div className="mt-4 flex flex-col gap-2 text-[12px]">
          <SummaryRow label={t("feeSummaryPlanLabel")}    value={t("feeSummaryPlanValue")} />
          <SummaryRow label={t("feeSubOrbitraId")}        value={`USD ${usd(ORBITRAID_YEAR)} ${t("feeSummaryYearSuffix")}`} />
          <SummaryRow label={t("feeSubServices")}         value={`USD ${usd(SME_MONTH)} ${t("feeSummaryMonthSuffix")}`} />
          <SummaryRow label={t("feeSummaryVatLabel")}     value={`USD ${usd(TOTAL_VAT)}`} />
          <SummaryRow label={t("feeSubChargedToday")}     value={`USD ${usd(TOTAL_GROSS)}`} />
          <SummaryRow label={t("feeSubNextCharge")}       value={`${t("feeSummaryNextChargeDate")} USD ${usd(SME_MONTH * (1 + VAT_RATE))}`} />
          <SummaryRow label={t("feeSubAutoRenew")}        value={t("feeSummaryAutoRenewOn")} />
          <SummaryRow label={t("feeSubCurrency")}         value="USD" />
        </div>

        {cardPhase === "approved" && (
          <p className="mt-auto inline-flex items-center gap-2 self-start rounded-full border border-policy-pass/40 bg-policy-pass/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-policy-pass">
            {t("feePaidChip")}
          </p>
        )}
      </div>

      {modalOpen && (
        <div
          className="fixed inset-0 z-50 grid place-items-center bg-orbitra-ink/80 backdrop-blur-sm"
          onClick={closeModal}
        >
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="checkout-modal-title"
            className="glass relative w-[460px] max-w-[92vw] p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              aria-label={t("feeModalCloseLabel")}
              onClick={closeModal}
              className="absolute end-3 top-2 text-[18px] leading-none text-orbitra-muted hover:text-orbitra-paper"
            >
              ×
            </button>

            <p id="checkout-modal-title" className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("feeModalKicker")}
            </p>
            <div className="mt-2 rounded-md border border-state-verified/30 bg-orbitra-ink/40 p-3 text-[11px]">
              <div className="flex items-center justify-between">
                <span className="text-orbitra-paper">{t("feeModalOrbitraIdRow")}</span>
                <span className="font-mono text-orbitra-paper">USD {usd(ORBITRAID_YEAR)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between">
                <span className="text-orbitra-paper">{t("feeModalSmeRow")}</span>
                <span className="font-mono text-orbitra-paper">USD {usd(SME_MONTH)}</span>
              </div>
              <div className="mt-1 flex items-center justify-between text-orbitra-muted">
                <span>{t("feeModalVatRow")}</span>
                <span className="font-mono">USD {usd(TOTAL_VAT)}</span>
              </div>
              <div className="my-2 border-t border-state-verified/20" />
              <div className="flex items-center justify-between font-semibold text-orbitra-paper">
                <span>{t("feeModalTotalRow")}</span>
                <span className="font-mono">USD {usd(TOTAL_GROSS)}</span>
              </div>
            </div>

            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">
              {t("feeCardHeader")}
            </p>
            <CardField label={t("feeCardNumberLabel")}  value={t("feeCardNumberValue")}  mono />
            <div className="mt-3 grid grid-cols-2 gap-3">
              <CardField label={t("feeCardExpiryLabel")} value={t("feeCardExpiryValue")} mono />
              <CardField label={t("feeCardCvcLabel")}    value={t("feeCardCvcValue")}    mono />
            </div>
            <CardField label={t("feeCardholderLabel")} value={t("feeCardholderValue")} />

            <button
              type="button"
              onClick={cardPhase === "approved" ? advanceScreen : undefined}
              aria-disabled={cardPhase !== "card" && cardPhase !== "approved"}
              className={clsx(
                "mt-5 w-full py-2.5 text-[12px]",
                cardPhase === "card"        && "cta-primary",
                cardPhase === "authorising" && "rounded-md bg-state-verified/40 font-display font-semibold text-orbitra-paper",
                cardPhase === "approved"    && "cta-primary rounded-md bg-policy-pass font-display font-semibold text-orbitra-ink",
              )}
            >
              {cardPhase === "card"        && `${t("feeAuthoriseCta")} ${usd(TOTAL_GROSS)} →`}
              {cardPhase === "authorising" && t("feeAuthorisingCta")}
              {cardPhase === "approved"    && t("feeApprovedCta")}
            </button>

            {cardPhase === "approved" && (
              <p className="mt-3 inline-flex items-center gap-2 self-start rounded-full border border-policy-pass/40 bg-policy-pass/10 px-3 py-1 font-mono text-[10px] font-semibold uppercase tracking-wider text-policy-pass">
                {t("feePaidChipLong")} <LtrToken>ch_orb_demo_4f7a1c</LtrToken>
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanTile({
  title,
  subtitle,
  highlights,
  footer,
  selected,
}: {
  kind: "freemium" | "sme";
  title: string;
  subtitle: string;
  highlights: { label: string; value: string }[];
  footer: string;
  selected: boolean;
}) {
  const t = useTranslations("t1Registration");
  return (
    <div className={clsx(
      "relative flex flex-col gap-3 rounded-md border p-4 text-[12px] transition-state",
      selected
        ? "border-state-verified bg-state-verified/12 shadow-[0_0_18px_var(--state-verified-glow)]"
        : "border-orbitra-muted/30 bg-orbitra-ink/30 opacity-80",
    )}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className={clsx("font-display text-[13px] font-semibold", selected ? "text-state-verified" : "text-orbitra-paper")}>
            {title}
          </p>
          <p className="mt-0.5 text-[10px] text-orbitra-muted">{subtitle}</p>
        </div>
        {selected && (
          <span className="rounded-full border border-state-verified/50 bg-state-verified/15 px-2 py-0.5 font-mono text-[8px] uppercase tracking-[0.18em] text-state-verified">
            {t("feePlanSelectedBadge")}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-1.5">
        {highlights.map((h) => (
          <div key={h.label} className="flex items-baseline justify-between gap-2">
            <span className="text-[10px] uppercase tracking-[0.16em] text-orbitra-muted">{h.label}</span>
            <span className="font-mono text-[11px] text-orbitra-paper">{h.value}</span>
          </div>
        ))}
      </div>

      <p className="mt-auto text-[10px] leading-snug text-orbitra-muted">{footer}</p>
    </div>
  );
}

function Note({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-orbitra-teal-soft">{label}</p>
      <p className="mt-0.5 text-[10px] leading-snug text-orbitra-muted">{children}</p>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between border-b border-state-verified/10 pb-1.5 last:border-b-0">
      <span className="text-orbitra-teal-soft">{label}</span>
      <span className="font-semibold text-orbitra-paper">{value}</span>
    </div>
  );
}

function CardField({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="mt-3">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <div className={clsx("mt-1 rounded-md border border-state-verified/30 bg-orbitra-ink/60 px-3 py-2 text-[12px] text-orbitra-paper", mono && "font-mono")}>
        {value}
      </div>
    </div>
  );
}
